// The only code frontend and backend share: request/response shapes for /auth and
// /reporting/v1. Field meanings follow docs/reporting-api-contract.md.
import { z } from 'zod';

export const CONTRACT_VERSION = '1';

// The twelve CEO cards, in the mock's card order (ceo-k-0 … ceo-k-11).
export const CEO_METRIC_IDS = [
  'activePlayers',
  'newRegistrations',
  'firstTimeDepositors',
  'ftdConversion',
  'deposits',
  'withdrawals',
  'ggr',
  'ngr',
  'secondDepositRate',
  'day7PlayerRetention',
  'average30DayLtv',
  'marketingRoi',
] as const;
export const ceoMetricIdSchema = z.enum(CEO_METRIC_IDS);
export type CeoMetricId = z.infer<typeof ceoMetricIdSchema>;

// Which gold the numbers come from: DS's ("ds", its observed summary) or ours ("ours",
// sr_ceo_dev.gold, built by pipeline/ceo_gold.sql to management's rules).
export const DATA_SET_IDS = ['ds', 'ours'] as const;
export const dataSetSchema = z.enum(DATA_SET_IDS);
export type DataSetId = z.infer<typeof dataSetSchema>;

// ready: management's rule, fully decided. partial: management's rule, with some parts on the
// sheet's "default until confirmed" (listed in missingInputs). observed: a raw source count that DS
// publishes, not yet management's rule. The rest carry no number.
export const resultStatusSchema = z.enum(['ready', 'partial', 'observed', 'unavailable', 'immature', 'notApplicable']);
export type ResultStatus = z.infer<typeof resultStatusSchema>;

export const missingInputSchema = z.object({
  code: z.string(),
  description: z.string(),
});
export type MissingInput = z.infer<typeof missingInputSchema>;

// A number sent as text: a decimal, optionally with an exponent ("1.5e-7"). "" or "n/a" never
// passes, so a missing value can't reach the page as 0.
const decimalSchema = z.string().regex(/^-?\d+(\.\d+)?([eE][-+]?\d+)?$/);

export const freshnessSchema = z.object({
  status: z.enum(['fresh', 'stale', 'unknown']),
});

export const metricResultSchema = z.object({
  metricId: ceoMetricIdSchema,
  definitionVersion: z.string().nullable(),
  status: resultStatusSchema,
  // Money and ratios travel as decimal strings, counts as integers; null = not calculable.
  value: z.union([decimalSchema, z.number().int()]).nullable(),
  numerator: z.union([decimalSchema, z.number().int()]).nullable(),
  denominator: z.union([decimalSchema, z.number().int()]).nullable(),
  // amount: a sum in the source currency named by `currency` (not converted to USD).
  unit: z.enum(['count', 'usd', 'ratio', 'amount']),
  currency: z.string().nullable(),
  // What the number counts, in words; shown on hover. null when there is no number.
  basis: z.string().nullable(),
  freshness: freshnessSchema,
  missingInputs: z.array(missingInputSchema),
});
export type MetricResult = z.infer<typeof metricResultSchema>;

const isoUtc = z.iso.datetime({ offset: false });

// The CEO pane's query: the Revenue Trend and Top Games take it as it is.
export const ceoQuerySchema = z
  .object({
    dataSet: dataSetSchema.default('ds'),
    // Half-open UTC range [from, to). No from = all time.
    from: isoUtc.optional(),
    to: isoUtc,
    // A value from /capabilities filters (game: a game's id); omitted = All.
    currency: z.string().min(1).max(32).optional(),
    game: z.string().min(1).max(200).optional(),
    compareFrom: isoUtc.optional(),
    compareTo: isoUtc.optional(),
  })
  .strict();
export type CeoQuery = z.infer<typeof ceoQuerySchema>;
// The CRM pane's player picks (proposals, CEO sheet Q27 and Q28). Its cards are the summary's, so the
// summary takes them too; the CEO pane sends neither.
const crmPicks = {
  // new, active, risk (At Risk and High Churn Risk) or churn; anything else is refused (422) with why.
  segment: z.string().min(1).max(32).optional(),
  // Players whose favourite game is this one (a game's id from /capabilities), or `none`: no real-money
  // bet in the 90 days before the data time.
  favouriteGame: z.string().min(1).max(200).optional(),
};
export const ceoSummaryQuerySchema = ceoQuerySchema.extend(crmPicks).strict();
export type CeoSummaryQuery = z.infer<typeof ceoSummaryQuerySchema>;

export const snapshotSchema = z.object({
  snapshotId: z.string().nullable(),
  // When the data was current: DS gold, its summary's as_of_utc; our gold, the run's silver_as_of.
  // null = unknown.
  dataAsOf: isoUtc.nullable(),
  freshness: freshnessSchema,
});
export type Snapshot = z.infer<typeof snapshotSchema>;

export const ceoSummaryResponseSchema = snapshotSchema.extend({
  contractVersion: z.literal(CONTRACT_VERSION),
  requestId: z.string(),
  generatedAt: isoUtc,
  filters: ceoSummaryQuerySchema.extend({ timezone: z.literal('UTC') }),
  status: resultStatusSchema,
  missingInputs: z.array(missingInputSchema),
  results: z.array(metricResultSchema),
});
export type CeoSummaryResponse = z.infer<typeof ceoSummaryResponseSchema>;

export const capabilitiesResponseSchema = snapshotSchema.extend({
  contractVersion: z.literal(CONTRACT_VERSION),
  environment: z.string(),
  // The data set these capabilities describe (GET /reporting/v1/capabilities?dataSet=...).
  dataSet: dataSetSchema,
  dataSets: z.array(z.object({ id: dataSetSchema, label: z.string() })),
  dataSource: z.string(),
  // Filter values the bridge can apply; an empty list means only "All" works today.
  filters: z.object({
    country: z.array(z.string()),
    source: z.array(z.string()),
    // By game id, not name: a renamed game is one game, and two games may share a name.
    game: z.array(z.object({ id: z.string(), label: z.string() })),
    currency: z.array(z.string()),
  }),
  metrics: z.array(z.object({ metricId: ceoMetricIdSchema, status: resultStatusSchema })),
  limits: z.object({ maxRangeDays: z.number().int() }),
});
export type CapabilitiesResponse = z.infer<typeof capabilitiesResponseSchema>;

// GET /reporting/v1/funnel — the Funnel pane. Both data sets count a registration cohort: the
// players who registered in [from, to), followed through their later steps so far.
export const FUNNEL_CARD_IDS = [
  'trackingLinkOpens',
  'registrationCompletionRate',
  'ftdConversion',
  'secondDepositRate',
] as const;
export const FUNNEL_STAGE_IDS = [
  'trackingLinkOpened',
  'registrationStarted',
  'registrationCompleted',
  'firstDeposit',
  'firstWager',
  'secondDeposit',
  'day7Active',
] as const;
export const FUNNEL_GAP_IDS = [
  'linkOpenToRegistrationStart',
  'registrationStartToComplete',
  'registrationToFirstDeposit',
  'firstDepositToFirstWager',
  'firstDepositToSecondDeposit',
] as const;

const funnelFigure = <T extends readonly [string, ...string[]]>(ids: T) =>
  z.object({
    id: z.enum(ids),
    status: resultStatusSchema,
    // Counts as integers; rates and seconds as decimal strings; null = no number.
    value: z.union([decimalSchema, z.number().int()]).nullable(),
    // Rates: numerator ÷ denominator. Times: denominator = players measured.
    numerator: z.number().int().nullable(),
    denominator: z.number().int().nullable(),
    unit: z.enum(['count', 'ratio', 'seconds']),
    basis: z.string().nullable(),
    missingInputs: z.array(missingInputSchema),
  });
export type FunnelCardId = (typeof FUNNEL_CARD_IDS)[number];
export type FunnelStageId = (typeof FUNNEL_STAGE_IDS)[number];
export type FunnelGapId = (typeof FUNNEL_GAP_IDS)[number];

export const funnelQuerySchema = z
  .object({
    dataSet: dataSetSchema.default('ds'),
    from: isoUtc.optional(),
    to: isoUtc,
    // First Game: the game of the player's first real-money bet, a game id from /capabilities game.
    game: z.string().min(1).max(200).optional(),
    device: z.enum(['desktop', 'mobile']).optional(),
    // The CEO pane's Currency filter, for its Player Funnel: the funnel has no amounts, so DS (not split
    // by currency) refuses it and our gold ignores it.
    currency: z.string().min(1).max(32).optional(),
  })
  .strict();
export type FunnelQuery = z.infer<typeof funnelQuerySchema>;

export const funnelResponseSchema = snapshotSchema.extend({
  contractVersion: z.literal(CONTRACT_VERSION),
  requestId: z.string(),
  generatedAt: isoUtc,
  filters: funnelQuerySchema.extend({ timezone: z.literal('UTC') }),
  definitionVersion: z.string().nullable(),
  // In the mock's order: the four cards, the seven funnel stages, the five timing rows.
  cards: z.array(funnelFigure(FUNNEL_CARD_IDS)),
  stages: z.array(funnelFigure(FUNNEL_STAGE_IDS)),
  gaps: z.array(funnelFigure(FUNNEL_GAP_IDS)),
  // Panels with no data yet, and why.
  unavailable: z.object({ dropOffBySource: z.array(missingInputSchema), actions: z.array(missingInputSchema) }),
});
export type FunnelResponse = z.infer<typeof funnelResponseSchema>;
export type FunnelFigure = FunnelResponse['cards' | 'stages' | 'gaps'][number];
export type FunnelResult = Pick<FunnelResponse, 'definitionVersion' | 'cards' | 'stages' | 'gaps' | 'unavailable'>;

// GET /reporting/v1/ceo/trends — the daily series behind the CEO pane's Revenue Trend, one value per
// UTC day from `days[0]`; the page groups them as the mock does. The CEO pane's query.
const seriesSchema = z.object({
  status: resultStatusSchema,
  // Aligned with days; null = unknown that day (a missing amount), never a silent 0.
  values: z.array(decimalSchema.nullable()),
  basis: z.string().nullable(),
  missingInputs: z.array(missingInputSchema),
});
export type Series = z.infer<typeof seriesSchema>;
export const ceoTrendsResponseSchema = snapshotSchema.extend({
  contractVersion: z.literal(CONTRACT_VERSION),
  requestId: z.string(),
  generatedAt: isoUtc,
  filters: ceoQuerySchema.extend({ timezone: z.literal('UTC') }),
  days: z.array(z.iso.date()),
  series: z.object({ deposits: seriesSchema, ggr: seriesSchema, ngr: seriesSchema }),
});
export type CeoTrendsResponse = z.infer<typeof ceoTrendsResponseSchema>;
export type CeoTrends = Pick<CeoTrendsResponse, 'days' | 'series'>;

// GET /reporting/v1/ceo/breakdowns?by=game — the CEO pane's Top Games.
export const ceoBreakdownQuerySchema = ceoQuerySchema.extend({ by: z.enum(['game']) }).strict();
export type CeoBreakdownQuery = z.infer<typeof ceoBreakdownQuerySchema>;
export const ceoBreakdownResponseSchema = snapshotSchema.extend({
  contractVersion: z.literal(CONTRACT_VERSION),
  requestId: z.string(),
  generatedAt: isoUtc,
  filters: ceoBreakdownQuerySchema.extend({ timezone: z.literal('UTC') }),
  status: resultStatusSchema,
  basis: z.string().nullable(),
  missingInputs: z.array(missingInputSchema),
  // Highest GGR first, one row per game id; game is its /capabilities label. null = rounds with no game id.
  rows: z.array(
    z.object({
      game: z.string().nullable(),
      players: z.number().int().nullable(),
      // Of those players, the ones who played it on two or more UTC days in the range.
      repeatPlayers: z.number().int().nullable(),
      ggr: decimalSchema.nullable(),
      missingInputs: z.array(missingInputSchema),
    }),
  ),
});
export type CeoBreakdownResponse = z.infer<typeof ceoBreakdownResponseSchema>;
export type CeoBreakdown = Pick<CeoBreakdownResponse, 'status' | 'basis' | 'missingInputs' | 'rows'>;

// GET /reporting/v1/crm — the CRM pane's Retention Cohort Trend and player health, and why its other
// panels have no data yet. Its measured cards (Day-7 retention, second-deposit rate, new and active
// players) are the CEO summary's, as on DS's own CRM page.
export const crmQuerySchema = z
  .object({
    dataSet: dataSetSchema.default('ds'),
    from: isoUtc.optional(),
    to: isoUtc,
    ...crmPicks,
    // The pane's VIP Tier and Campaign Response picks: neither can be applied yet (422).
    vipTier: z.string().min(1).max(32).optional(),
    campaignResponse: z.string().min(1).max(32).optional(),
  })
  .strict();
export type CrmQuery = z.infer<typeof crmQuerySchema>;

// The pane's panels that can lack data, in the mock's order. highChurnRisk, atRisk, churned, baseline,
// riskBreakdown and churnSignals are drawn from `health` when it has data.
export const CRM_PANEL_IDS = [
  'highChurnRisk',
  'reactivation',
  'atRisk',
  'churned',
  'baseline',
  'riskBreakdown',
  'exampleDeviation',
  'churnSignals',
  'attention',
  'campaigns',
  'actions',
] as const;
export type CrmPanelId = (typeof CRM_PANEL_IDS)[number];
// The mock's decline signals, in its order: the last one set is a player's main reason.
export const HEALTH_SIGNAL_IDS = [
  'wagerVolume',
  'bettingFrequency',
  'depositAmount',
  'depositFrequency',
  'noRecentWagers',
] as const;

const cohortDaySchema = z.object({
  // Of the group's players whose day N has passed at the data time (eligible), those who bet on it.
  eligible: z.number().int(),
  retained: z.number().int(),
  // retained ÷ eligible; null when nobody in the group has reached day N yet.
  value: decimalSchema.nullable(),
});
export const crmResponseSchema = snapshotSchema.extend({
  contractVersion: z.literal(CONTRACT_VERSION),
  requestId: z.string(),
  generatedAt: isoUtc,
  filters: crmQuerySchema.extend({ timezone: z.literal('UTC') }),
  definitionVersion: z.string().nullable(),
  retention: z.object({
    status: resultStatusSchema,
    basis: z.string().nullable(),
    missingInputs: z.array(missingInputSchema),
    // One cohort per UTC sign-up day of the range (all time: from the first sign-up's day), oldest first;
    // a day nobody signed up on has registrations 0 (management, Q11).
    cohorts: z.array(
      z.object({
        from: z.iso.date(),
        registrations: z.number().int(),
        day1: cohortDaySchema,
        day7: cohortDaySchema,
        day30: cohortDaySchema,
      }),
    ),
  }),
  // Player health at the data time: the date filter doesn't scope it, as the mock says.
  health: z.object({
    status: resultStatusSchema,
    basis: z.string().nullable(),
    missingInputs: z.array(missingInputSchema),
    // The rule's windows in days, and the fewest signals that flag a player. null when unavailable.
    rule: z
      .object({
        baselineDays: z.number().int(),
        wagerDays: z.number().int(),
        depositDays: z.number().int(),
        churnDays: z.number().int(),
        flagSignals: z.number().int(),
        // CRM Actions' win-back pool: no real-money bet for this many days (the mock's 30; Q28 proposal).
        dormantDays: z.number().int(),
      })
      .nullable(),
    // Players whose last real-money bet is dormantDays or more before the data time; never-bettors are
    // not counted. null when unavailable.
    dormant: z.number().int().nullable(),
    // Players by health; notScored are too new or have no history to compare. null when unavailable.
    players: z
      .object({
        healthy: z.number().int(),
        atRisk: z.number().int(),
        highChurnRisk: z.number().int(),
        churned: z.number().int(),
        notScored: z.number().int(),
      })
      .nullable(),
    // Of the flagged players (At Risk, High Churn Risk, Churned), in the mock's order: how many each
    // signal fired for, and how many have it as their main reason. Empty when unavailable.
    signals: z.array(z.object({ id: z.enum(HEALTH_SIGNAL_IDS), triggered: z.number().int(), main: z.number().int() })),
  }),
  // The panels with no data, each with why; a panel left out has its data (the health panels in `health`).
  unavailable: z.partialRecord(z.enum(CRM_PANEL_IDS), z.array(missingInputSchema)),
});
export type CrmResponse = z.infer<typeof crmResponseSchema>;
export type CrmResult = Pick<CrmResponse, 'definitionVersion' | 'retention' | 'health' | 'unavailable'>;
export type CrmHealth = CrmResponse['health'];
export type CrmCohort = CrmResponse['retention']['cohorts'][number];

// GET /reporting/v1/definitions — the metric registry. approved: false means the card can't
// produce a value yet, whatever the data says.
export const definitionsResponseSchema = z.object({
  contractVersion: z.literal(CONTRACT_VERSION),
  definitions: z.array(
    z.object({
      metricId: ceoMetricIdSchema,
      approved: z.boolean(),
      definitionVersion: z.string().nullable(),
      unit: z.enum(['count', 'usd', 'ratio', 'amount']),
      missingInputs: z.array(missingInputSchema),
    }),
  ),
});
export type DefinitionsResponse = z.infer<typeof definitionsResponseSchema>;

export const loginRequestSchema = z.object({
  username: z.string().min(1).max(64),
  password: z.string().min(1).max(128),
});
export type LoginRequest = z.infer<typeof loginRequestSchema>;

// Which brands a dashboard user may see: "all", or a list of gold brand_id values.
export const scopeSchema = z.union([z.literal('all'), z.array(z.string())]);
export type Scope = z.infer<typeof scopeSchema>;

export const sessionUserSchema = z.object({
  username: z.string(),
  scope: scopeSchema,
});
export type SessionUser = z.infer<typeof sessionUserSchema>;

export const errorResponseSchema = z.object({
  error: z.object({ code: z.string(), message: z.string(), requestId: z.string() }),
});
export type ErrorResponse = z.infer<typeof errorResponseSchema>;
